// A team of one's own progress (D-169): this week so far against the same days last week, and her calls week by week.
// Pure functions, tested.
import { fmtTalk, teamTotals, type Totals } from "./dashboard";
import { addDays } from "./range";
import type { DashBucket, DashMember } from "./types";

/** The Monday of the week a day is in. */
export function weekStart(day: string): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
}

/** This week so far, and the same days of last week, so a Wednesday is weighed against a Wednesday. */
export function weekWindows(today: string) {
  const from = weekStart(today);
  return { now: { from, to: today }, before: { from: addDays(from, -7), to: addDays(today, -7) } };
}

/** How a number moved: "up 18%", "down 4 points", "same", or "new" against nothing; better says which way is good. */
export interface Change {
  text: string;
  better: boolean | null;
}

export function countChange(now: number, before: number): Change {
  if (now === before) return { text: "same as last week", better: null };
  if (before === 0) return { text: "none last week", better: now > 0 };
  const percent = Math.round(((now - before) / before) * 100);
  if (percent === 0) return { text: "same as last week", better: null };
  return { text: `${percent > 0 ? "up" : "down"} ${Math.abs(percent)}%`, better: percent > 0 };
}

/** Shares compared in points: 61% against 57% is "up 4 points". Null when either has nothing to divide. */
export function shareChange(now: number | null, before: number | null): Change {
  if (now === null || before === null) return { text: "", better: null };
  const points = Math.round((now - before) * 100);
  if (points === 0) return { text: "same as last week", better: null };
  return {
    text: `${points > 0 ? "up" : "down"} ${Math.abs(points)} ${Math.abs(points) === 1 ? "point" : "points"}`,
    better: points > 0,
  };
}

const share = (part: number, whole: number) => (whole > 0 ? part / whole : null);
const percent = (s: number | null) => (s === null ? "–" : `${Math.round(s * 100)}%`);

export interface ProgressStat {
  label: string;
  value: string;
  change: Change;
}

/** Her week in six numbers, each against last week. */
export function weekStats(now: DashMember[], before: DashMember[]): ProgressStat[] {
  const a: Totals = teamTotals(now);
  const b: Totals = teamTotals(before);
  const connected = (t: Totals) => share(t.dialed_connected, t.dialed);
  const onTime = (t: Totals) => share(t.follow_ups_on_time, t.follow_ups_done);
  return [
    {
      label: "Calls",
      value: String(a.dialed + a.received),
      change: countChange(a.dialed + a.received, b.dialed + b.received),
    },
    { label: "Connected", value: percent(connected(a)), change: shareChange(connected(a), connected(b)) },
    { label: "Talk time", value: fmtTalk(a.talk_seconds), change: countChange(a.talk_seconds, b.talk_seconds) },
    {
      label: "Customers reached",
      value: String(a.customers_reached),
      change: countChange(a.customers_reached, b.customers_reached),
    },
    {
      label: "Interested",
      value: String(a.interested),
      change: countChange(a.interested, b.interested),
    },
    { label: "Follow-ups on time", value: percent(onTime(a)), change: shareChange(onTime(a), onTime(b)) },
  ];
}

/** Calls and connected calls in each of the weeks from a Monday on, oldest first, quiet weeks included. */
export function callsByWeek(days: DashBucket[], from: string, weeks: number) {
  const out = Array.from({ length: weeks }, (_, i) => ({ week: addDays(from, i * 7), calls: 0, connected: 0 }));
  for (const d of days) {
    if (!d.day) continue;
    const i = Math.floor((Date.parse(`${d.day}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / (7 * 86_400_000));
    if (i < 0 || i >= weeks) continue;
    out[i].calls += d.calls;
    out[i].connected += d.connected;
  }
  return out;
}
